import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';

interface CustomerPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

/**
 * Customer Portal UI refinement — a fixed-palette fork of the shared
 * `Pagination` component. The shared component's classes (`bg-card`,
 * `text-foreground`, `bg-primary`) resolve through hsl(var(--x)) CSS
 * variables that repaint dark under the app's shared .dark theme class —
 * fine for staff/platform screens, wrong here since the Customer Portal
 * uses one fixed light palette regardless of theme, exactly the same
 * reasoning already documented in CustomerSearchBar.tsx. Same props/
 * behavior as Pagination, customer-* design tokens instead.
 */
export function CustomerPagination({
  page,
  pageSize,
  total,
  onPageChange,
}: CustomerPaginationProps) {
  const totalPages = Math.ceil(total / pageSize);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  if (total === 0) return null;

  /**
   * `shrink-0` is load-bearing, not decoration. These buttons are flex items:
   * `w-11` is a preferred width, not a floor, so when the row is a few pixels
   * wider than a 320px viewport allows, flex silently shaves the difference off
   * every button. The authenticated E2E measured exactly that — 43.11px instead
   * of 44px on the Jobs list at 320px, with two of the four controls enabled.
   * With `shrink-0` the 44px target size is guaranteed at every width; the row
   * gives up its gap instead (see the container below).
   */
  const btnClass = (disabled: boolean) =>
    cn(
      'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-customer-border text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring',
      disabled
        ? 'cursor-not-allowed opacity-40 bg-customer-surface text-customer-text-secondary'
        : 'bg-customer-surface text-customer-text hover:bg-customer-bg cursor-pointer',
    );

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-customer-text-secondary">
        Showing{' '}
        <span className="font-medium text-customer-text">
          {from}–{to}
        </span>{' '}
        of <span className="font-medium text-customer-text">{total}</span> records
      </p>
      {/* The gap tightens to 2px below `sm` so that four 44px targets, the page
          indicator and "of N" still fit inside a 320px viewport once the buttons
          can no longer shrink. From `sm:` up the original 4px gap is restored,
          so nothing changes on the widths that already had room. */}
      <div className="flex items-center gap-0.5 sm:gap-1" role="navigation" aria-label="Pagination">
        <button
          className={btnClass(page <= 1)}
          onClick={() => onPageChange(1)}
          disabled={page <= 1}
          aria-label="First page"
        >
          <FontAwesomeIcon icon={faAnglesLeft} className="h-3 w-3" />
        </button>
        <button
          className={btnClass(page <= 1)}
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
        </button>
        <span className="flex h-11 min-w-[2.75rem] items-center justify-center rounded-lg bg-customer-primary px-2 text-sm font-medium text-customer-action-ink">
          {page}
        </span>
        <span className="px-1 text-sm text-customer-text-secondary">of {totalPages}</span>
        <button
          className={btnClass(page >= totalPages)}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
        </button>
        <button
          className={btnClass(page >= totalPages)}
          onClick={() => onPageChange(totalPages)}
          disabled={page >= totalPages}
          aria-label="Last page"
        >
          <FontAwesomeIcon icon={faAnglesRight} className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
